import { z } from 'zod';

/**
 * Sessao 52b: faturamento dos 12 meses anteriores (RBT12) e folha dos 12 meses (fator R) da empresa por competencia, DIGITADOS.
 * Base da aliquota efetiva do Simples CALCULADA (`perfilTributario.simples.modoEfetiva = 'CALCULADA'`). Sem sugestao automatica a partir
 * da receita do sistema: nao ha fonte de receita 12m confiavel (docs/SESSAO52-DECISOES.md).
 */
const reais2 = z.string().regex(/^\d{1,16}(\.\d{1,2})?$/, 'Valor invalido (R$, ate 2 casas, sem sinal, use ponto)');

/** Competencia na URL: `YYYY-MM` ou `YYYY-MM-01` (sempre o 1o dia do mes). */
export const competenciaSimplesParam = z.object({
  competencia: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])(-01)?$/, 'Competencia invalida (use YYYY-MM)'),
});

/** PUT /empresa/simples-apuracao/:competencia (upsert). */
export const salvarSimplesApuracaoSchema = z.object({
  rbt12: reais2,
  /** Folha de salarios + pro-labore + encargos dos 12 meses (fator R). Nulo = nao informada (so e exigida para servico em FATOR_R). */
  folha12: reais2.nullish(),
});
export type SalvarSimplesApuracaoDto = z.infer<typeof salvarSimplesApuracaoSchema>;

export interface SimplesApuracaoResponse {
  /** 1o dia do mes (YYYY-MM-01). */
  competencia: string;
  rbt12: string;
  folha12: string | null;
  /** folha12 / rbt12 (fracao, 8 casas); nulo sem folha ou com RBT12 zerado. */
  fatorR: string | null;
  origem: 'DIGITADO';
  atualizadoEm: string;
}

export interface SimplesApuracaoListaResponse {
  /** Competencia (YYYY-MM-01) do mes de hoje. */
  competenciaAtual: string;
  /** A apuracao que vale hoje (a do mes ou a mais recente anterior); nula = nenhuma (o Simples CALCULADA recusa com SIMPLES_RBT12_AUSENTE). */
  vigente: SimplesApuracaoResponse | null;
  itens: SimplesApuracaoResponse[];
}

/**
 * `simples` das respostas de formacao/lista/orcamento: como a efetiva do Simples foi calculada para o item. So existe com `modoEfetiva = CALCULADA`
 * (ausente na efetiva digitada). Decimais como string.
 */
export interface SimplesResumoResponse {
  /** Anexo escolhido para o item (bem: anexoBem; servico: anexoServico, ou III/V pelo fator R). */
  anexo: 'I' | 'II' | 'III' | 'IV' | 'V';
  /** O que foi configurado na tabela para este tipo de item (I, II, III, V ou FATOR_R). */
  escolha: 'I' | 'II' | 'III' | 'V' | 'FATOR_R';
  faixa: number;
  aliquotaNominal: string;
  parcelaDeduzir: string;
  rbt12: string;
  /** Efetiva cheia do DAS: (RBT12 x nominal - parcela a deduzir) / RBT12. */
  efetiva: string;
  /** DAS que entrou no preco: SIMPLES = a efetiva; SIMPLES_HIBRIDO = DAS sem CBS/IBS (calculado ou, a confirmar, o digitado). */
  dasNoPreco: string;
  /** DAS HIBRIDO: CALCULADA (partilha confirmada) ou DIGITADA (a confirmar: usou o `dasEfetivaSemCbsIbs` do perfil). SIMPLES: sempre CALCULADA. */
  dasOrigem: 'CALCULADA' | 'DIGITADA';
  /** Partilha de CBS/IBS (LC 227/2026 Anexo XX) confirmada em fonte oficial para este anexo/faixa. */
  cbsIbsConfirmado: boolean;
  /** Competencia do RBT12 usado (YYYY-MM-01) e a folha/fator R, quando houve. */
  competencia: string;
  folha12: string | null;
  fatorR: string | null;
  /** Tabela legal usada: inicio da vigencia e URL da fonte. */
  tabelaLegal: { vigenciaInicio: string; fonteUrl: string };
  avisos: Array<{ codigo: string; mensagem: string }>;
}
